import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3,
  Users,
  Activity,
  AlertTriangle,
  TrendingUp,
  Eye,
  Clock,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  MessageSquare,
  Home,
  Bot,
  Calculator,
  Shield,
  Zap,
  Target,
  PieChart,
  ChevronLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [overview, setOverview] = useState(null);
  const [tools, setTools] = useState(null);
  const [daily, setDaily] = useState(null);
  const [users, setUsers] = useState(null);
  const [errors, setErrors] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
    // Refresh realtime every 30 seconds
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    const opts = { withCredentials: true };

    // Fetch each endpoint individually to avoid one failure breaking everything.
    // Log failures so real errors don't stay silent (previously masked as null data).
    const safeGet = async (url) => {
      try {
        return await axios.get(url, opts);
      } catch (err) {
        console.error(`[Analytics] Failed ${url}:`, err.response?.status, err.response?.data);
        return { data: null, _error: err };
      }
    };

    try {
      const overviewRes = await safeGet(`${API}/api/analytics/admin/overview?days=${period}`);
      const toolsRes = await safeGet(`${API}/api/analytics/admin/tools?days=${period}`);
      const dailyRes = await safeGet(`${API}/api/analytics/admin/daily?days=${period}`);
      const usersRes = await safeGet(`${API}/api/analytics/admin/users?days=${period}`);
      const errorsRes = await safeGet(`${API}/api/analytics/admin/errors?days=7`);
      const engagementRes = await safeGet(`${API}/api/analytics/admin/engagement?days=${period}`);
      const realtimeRes = await safeGet(`${API}/api/analytics/admin/realtime`);

      setOverview(overviewRes.data);
      setTools(toolsRes.data);
      setDaily(dailyRes.data);
      setUsers(usersRes.data);
      setErrors(errorsRes.data);
      setEngagement(engagementRes.data);
      setRealtime(realtimeRes.data);

      // If EVERY endpoint failed with 403, redirect (not a founder)
      const forbidden = [overviewRes, toolsRes, dailyRes, usersRes, errorsRes, engagementRes]
        .every(r => r._error?.response?.status === 403);
      if (forbidden) {
        toast.error('Accès réservé au fondateur');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtime = async () => {
    try {
      const res = await axios.get(`${API}/api/analytics/admin/realtime`, { withCredentials: true });
      setRealtime(res.data);
    } catch (e) {
      console.error('[Analytics] fetchRealtime failed:', e.response?.status);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-600`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
            {Math.abs(trend)}% vs période précédente
          </div>
        )}
      </CardContent>
    </Card>
  );

  const toolIcons = {
    'calculator': Calculator,
    'mdb': Target,
    'ai_agent': Bot,
    'cadastre': Home,
    'diagnostic_climatique': Activity,
    'community': Users,
    'messages': MessageSquare,
    'classroom': FileText,
    'events': Calendar,
    'dashboard': BarChart3
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 mb-3 sm:mb-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10 flex-shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Retour</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 truncate">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" /> Admin Analytics
              </h1>
              <p className="text-slate-300 text-xs sm:text-sm hidden sm:block">Surveillance complète de la plateforme</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 ml-0 sm:ml-auto">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 sm:w-40 bg-white/10 border-white/20 text-white text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 derniers jours</SelectItem>
                <SelectItem value="30">30 derniers jours</SelectItem>
                <SelectItem value="90">90 derniers jours</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchAllData} variant="outline" size="sm" className="text-white border-white/20 hover:bg-white/10 text-xs sm:text-sm">
              <RefreshCw className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Actualiser</span>
            </Button>
          </div>
         </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Realtime Banner */}
        {realtime && (
          <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm font-medium text-green-700">En temps réel</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600">
                    <span className="font-bold text-green-600">{realtime.last_hour?.active_users || 0}</span> actifs
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600">
                    <span className="font-bold text-blue-600">{realtime.last_hour?.events || 0}</span> events/h
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600">
                    <span className={`font-bold ${realtime.last_hour?.errors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {realtime.last_hour?.errors || 0}
                    </span> erreurs/h
                  </div>
                </div>
                <span className="text-xs text-slate-400">
                  Mis à jour: {new Date(realtime.timestamp).toLocaleTimeString('fr-FR')}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Tabs */}
        <div className="flex gap-1.5 sm:gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
            { id: 'tools', label: 'Outils', icon: Zap },
            { id: 'users', label: 'Utilisateurs', icon: Users },
            { id: 'engagement', label: 'Engagement', icon: Activity },
            { id: 'errors', label: 'Erreurs', icon: AlertTriangle }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className="whitespace-nowrap text-xs sm:text-sm"
            >
              <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Utilisateurs totaux"
                value={overview?.users?.total || 0}
                subtitle={`+${overview?.users?.new || 0} nouveaux`}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Utilisateurs actifs"
                value={overview?.users?.active || 0}
                subtitle={`Sur ${period} jours`}
                icon={Activity}
                color="green"
              />
              <StatCard
                title="Membres Club"
                value={overview?.users?.club_members || 0}
                subtitle={`${overview?.users?.conversion_rate || 0}% conversion`}
                icon={Shield}
                color="purple"
              />
              <StatCard
                title="Événements"
                value={overview?.activity?.total_events || 0}
                subtitle={`~${overview?.activity?.avg_events_per_day || 0}/jour`}
                icon={Zap}
                color="amber"
              />
            </div>

            {/* Health Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Santé du Système
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg ${(overview?.health?.error_rate || 0) < 1 ? 'bg-green-50' : (overview?.health?.error_rate || 0) < 5 ? 'bg-amber-50' : 'bg-red-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Taux d'erreur</span>
                      {(overview?.health?.error_rate || 0) < 1 ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${(overview?.health?.error_rate || 0) < 1 ? 'text-green-600' : (overview?.health?.error_rate || 0) < 5 ? 'text-amber-600' : 'text-red-600'}`}>
                      {overview?.health?.error_rate || 0}%
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Erreurs totales</span>
                      <XCircle className="w-5 h-5 text-slate-400" />
                    </div>
                    <p className="text-2xl font-bold text-slate-700">{overview?.health?.total_errors || 0}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${(overview?.health?.unresolved_errors || 0) === 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Non résolues</span>
                      {(overview?.health?.unresolved_errors || 0) === 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${(overview?.health?.unresolved_errors || 0) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {overview?.health?.unresolved_errors || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Daily Chart */}
            {daily?.daily && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" /> Activité quotidienne
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const rows = daily.daily.slice(-30);
                    const maxEvents = Math.max(1, ...rows.map(d => d.events || 0));
                    const totalEvents = rows.reduce((s, d) => s + (d.events || 0), 0);
                    if (totalEvents === 0) {
                      return (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                          <TrendingUp className="w-10 h-10 mb-2 opacity-40" />
                          <p className="text-sm">Aucune activité sur la période</p>
                          <p className="text-xs mt-1">Les événements apparaîtront ici dès que les utilisateurs interagiront avec la plateforme.</p>
                        </div>
                      );
                    }
                    return (
                      <div className="h-64 flex items-end gap-1" data-testid="daily-chart">
                        {rows.map((day, idx) => (
                          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                              style={{ height: `${Math.min(((day.events || 0) / maxEvents) * 200, 200)}px` }}
                              title={`${day.date}: ${day.events} événements`}
                            />
                            {idx % 5 === 0 && (
                              <span className="text-xs text-slate-400 -rotate-45 origin-left">
                                {day.date?.slice(5)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-slate-600">Outil le plus utilisé</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700 capitalize">
                    {tools?.most_used?.replace('_', ' ') || 'N/A'}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-slate-600">Outil le moins utilisé</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-700 capitalize">
                    {tools?.least_used?.replace('_', ' ') || 'N/A'}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" /> Utilisation des outils
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tools?.tools?.length > 0 ? (
                  <div className="space-y-3">
                    {tools.tools.map((tool, idx) => {
                      const IconComponent = toolIcons[tool.tool] || Zap;
                      return (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-slate-700 capitalize">
                                {tool.tool?.replace('_', ' ')}
                              </span>
                              <span className="text-sm text-slate-500">
                                {tool.views} vues ({tool.percentage}%)
                              </span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full transition-all"
                                style={{ width: `${tool.percentage}%` }}
                              />
                            </div>
                          </div>
                          <Badge variant="outline">{tool.unique_users} users</Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Aucune donnée d'utilisation disponible</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Tier Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Répartition des utilisateurs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(users?.tier_distribution || {}).length > 0 ? (
                    Object.entries(users.tier_distribution).map(([tier, count]) => (
                      <div key={tier} className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-700">{count}</p>
                        <p className="text-sm text-slate-500 capitalize">{tier || 'Free'}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 col-span-4 text-center py-4">Aucune donnée disponible</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Most Active Users */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Utilisateurs les plus actifs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {users?.most_active_users?.length > 0 ? (
                  <div className="space-y-2">
                    {users.most_active_users.map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600">
                            {idx + 1}
                          </div>
                          <span className="text-sm text-slate-600">{user.email}</span>
                        </div>
                        <Badge>{user.events} événements</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune activité enregistrée</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Signups */}
            <Card>
              <CardHeader>
                <CardTitle>Inscriptions récentes</CardTitle>
              </CardHeader>
              <CardContent>
                {users?.recent_signups?.length > 0 ? (
                  <div className="space-y-2">
                    {users.recent_signups.slice(0, 5).map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-700">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={user.subscription_tier === 'club' ? 'default' : 'outline'}>
                            {user.subscription_tier || 'free'}
                          </Badge>
                          <p className="text-xs text-slate-400 mt-1">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune inscription récente</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Engagement Tab */}
        {activeTab === 'engagement' && (
          <div className="space-y-6">
            {/* Key Engagement Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard
                title="PDFs exportés"
                value={engagement?.key_metrics?.pdf_exports || 0}
                icon={FileText}
                color="blue"
              />
              <StatCard
                title="Recherches"
                value={engagement?.key_metrics?.searches || 0}
                icon={Eye}
                color="green"
              />
              <StatCard
                title="Messages"
                value={engagement?.key_metrics?.messages_sent || 0}
                icon={MessageSquare}
                color="purple"
              />
              <StatCard
                title="Biens publiés"
                value={engagement?.key_metrics?.properties_published || 0}
                icon={Home}
                color="amber"
              />
              <StatCard
                title="Requêtes IA"
                value={engagement?.key_metrics?.ai_queries || 0}
                icon={Bot}
                color="cyan"
              />
            </div>

            {/* Actions Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Détail des actions</CardTitle>
              </CardHeader>
              <CardContent>
                {engagement?.actions_breakdown?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {engagement.actions_breakdown.slice(0, 12).map((action, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-bold text-slate-700">{action.count}</p>
                        <p className="text-xs text-slate-500 capitalize">{action.action?.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune action enregistrée</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Errors Tab */}
        {activeTab === 'errors' && (
          <div className="space-y-6">
            {/* Error Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" /> Types d'erreurs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {errors?.error_types?.length > 0 ? (
                      errors.error_types.map((err, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                          <span className="text-sm text-slate-600">{err.type}</span>
                          <Badge variant="destructive">{err.count}</Badge>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-green-600">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                        <p>Aucune erreur détectée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Endpoints problématiques</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {errors?.problematic_endpoints?.length > 0 ? (
                      errors.problematic_endpoints.map((ep, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                          <span className="text-sm text-slate-600 font-mono">{ep.endpoint}</span>
                          <Badge variant="outline">{ep.errors} erreurs</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">Aucun endpoint problématique</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Errors */}
            <Card>
              <CardHeader>
                <CardTitle>Erreurs récentes (7 derniers jours)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {errors?.recent_errors?.length > 0 ? (
                    errors.recent_errors.map((err, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border-l-4 border-red-400">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-700">{err.error_type}</p>
                            <p className="text-sm text-slate-500 mt-1">{err.error_message}</p>
                            {err.endpoint && (
                              <p className="text-xs text-slate-400 font-mono mt-1">{err.endpoint}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">
                              {err.timestamp ? new Date(err.timestamp).toLocaleString('fr-FR') : 'N/A'}
                            </p>
                            <Badge variant={err.resolved ? 'default' : 'destructive'} className="mt-1">
                              {err.resolved ? 'Résolu' : 'Non résolu'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-green-600">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                      <p className="text-lg font-medium">Système stable</p>
                      <p className="text-sm text-slate-500">Aucune erreur ces 7 derniers jours</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
